import { formatCreditsWithUnit } from '@lib/utils/units';
import { COST_WATERMARK_REMOVAL_PER_MINUTE_IN_CREDITS, SECONDS_IN_MINUTE } from '@stores/Billing';
import { useUIPreferences } from '@stores/UIPreferences';
import { useCallback } from 'react';

export type WatermarkOption = 'yes' | 'no';

export interface UseRemoveWatermarkOptions {
  /** Total duration in seconds for cost calculation */
  totalSeconds: number;
}

export interface UseRemoveWatermarkReturn {
  /** Current watermark removal state */
  removeWatermark: WatermarkOption;
  /** Function to update watermark removal state */
  setRemoveWatermark: (value: WatermarkOption) => void;
  /** Calculated cost for watermark removal in credits */
  watermarkRemovalCost: number;
  /** Whether watermark is enabled (inverted: true when removeWatermark is 'no') */
  isWatermarkEnabled: boolean;
  /** Tooltip text for watermark removal cost - source of truth */
  watermarkRemovalTooltipText: string;
}

/**
 * Hook for managing watermark removal state and calculating removal costs.
 * The watermark preference is persisted to the user's account settings.
 *
 * @param options Configuration options for the hook
 * @returns Watermark removal state and utilities
 */
export function useRemoveWatermark({
  totalSeconds,
}: UseRemoveWatermarkOptions): UseRemoveWatermarkReturn {
  const uiPreferences = useUIPreferences();

  // Convert boolean from store to WatermarkOption
  const removeWatermark: WatermarkOption = uiPreferences.removeWatermark ? 'yes' : 'no';

  // Update both local state and persist to store
  const setRemoveWatermark = useCallback(
    (value: WatermarkOption) => {
      uiPreferences.setRemoveWatermark(value === 'yes');
    },
    [uiPreferences],
  );

  const watermarkRemovalCost = Math.ceil(
    (totalSeconds / SECONDS_IN_MINUTE) * COST_WATERMARK_REMOVAL_PER_MINUTE_IN_CREDITS,
  );

  // Inverted logic: if removeWatermark is 'no', then watermark is enabled (watermark is added)
  // If removeWatermark is 'yes', then watermark is disabled (watermark is removed)
  const isWatermarkEnabled = removeWatermark === 'no';

  const watermarkRemovalTooltipText = `Removing the StarZero watermark would cost ${formatCreditsWithUnit(watermarkRemovalCost, 'up')}.`;

  return {
    removeWatermark,
    setRemoveWatermark,
    watermarkRemovalCost,
    isWatermarkEnabled,
    watermarkRemovalTooltipText,
  };
}
